import PropTypes from 'prop-types'
import qs from 'query-string'
import { useMemo, useCallback, useState, useContext, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Layout } from '@loadsmart/miranda-react'

import { useWindowWidth } from 'core/ui/hooks/useWindowWidth'
import { goToLogin } from 'core/use-cases/goToLogin'
import { formatCurrency } from 'core/utils/currency'

import { LoadingV2 } from 'legacy/components/LoadingV2/LoadingV2'
import { Mileage } from 'legacy/components/LoadOffer/Mileage'
import { Price } from 'legacy/components/LoadOffer/Price'
import { StrikeThroughPrice } from 'legacy/components/LoadOffer/StrikeThroughPrice'
import { Tooltip } from 'legacy/components/Tooltip/Tooltip'
import { LoadBoardContext, LOAD_BOARD_ACTIONS } from 'legacy/contexts/loadBoard'
import { intSizes } from 'legacy/styles/screen'
import { getLoadOfferPriceBreakdown } from 'legacy/utils/getLoadOfferPriceBreakdown'
import { getLoadOfferType, OFFER_TYPES } from 'legacy/utils/getLoadOfferType'
import { user } from 'legacy/utils/user'

import { useConfigContext } from 'config/ui/ConfigContext'
import { shouldShowBidCountdown, shouldDisableBid, hasBidPriceChanged } from 'loads/domain/Bid'
import { getLoadBookingType } from 'loads/domain/LoadOffer'
import { useBookingOffer } from 'loads/ui/hooks/useBookingOffer'
import { useCountdown } from 'loads/ui/hooks/useCountdown'
import { OfferChatButton } from 'offers/ui/OfferChatButton'
import { RateBreakdown } from 'offers/ui/RateBreakdown'
import { RatePerMile } from 'offers/ui/RatePerMile'

import { useAwardedBid } from '../../../../../loads/ui/hooks/useAwardedBid'
import { BookLink } from '../../BookDialog/BookLink'
import { useAnalytics } from '../../hooks/useAnalytics'
import { getButtonLabel } from '../../presenters/awardedBidPresenter'
import {
  ActionsFooterInnerContainer,
  ActionsFooterItemGroupContainer,
  BookButton,
  BidButton,
  StyledSmallLeftIconClock,
  ActionsFooterPricing,
} from './ActionsFooter.styles'
import { ExpirationInfo } from './ExpirationInfo'

function presentButtonText(buttonText, { userIsLoggedIn, isSmallScreen }) {
  if (userIsLoggedIn) return buttonText
  if (isSmallScreen) return 'Sign In'
  return 'Sign in to see full details'
}

function checkPriceRange(min_price, max_price) {
  return Boolean(min_price && max_price)
}

function isBidAvailable(userIsLoggedIn, biddable, showOnboardingImprovements, isCarrierReady) {
  return userIsLoggedIn && biddable && (showOnboardingImprovements || isCarrierReady)
}

function isBidButtonDisabled(carrier, activeBid, showBidCountdown, showOnboardingImprovements) {
  const shouldDisable = shouldDisableBid({
    carrier,
    activeBid,
    showBidCountdown,
  })

  const shouldEnableForOnboarding = showOnboardingImprovements && !showBidCountdown

  return shouldDisable && !shouldEnableForOnboarding
}

function appendQueryParams(url, queryParams) {
  const { pathname, search } = url
  const queryStrings = Object.keys(queryParams)
    .map(key => `${key}=${queryParams[key]}`)
    .join('&')

  return search ? `${url}&${queryStrings}` : `${pathname}?${queryStrings}`
}

function ActionsFooter({ isLoading, load, setRejectModalOpen, onBook }) {
  const { uuid: loadUUID, active_offer, ref_number } = load

  const { uuid: offerUUID, price, max_price, min_price, biddable, bid_allowed } = active_offer || {}
  const { mileage, stops } = load || {}
  const offerType = useMemo(() => getLoadOfferType(load), [load])

  const userIsLoggedIn = useMemo(() => user.isLoggedIn(), [])
  const history = useHistory()
  const location = useLocation()
  const windowWidth = useWindowWidth()
  const isSmallScreen = windowWidth <= intSizes.md

  const [isOfferExpired, setOfferExpired] = useState(false)

  const [{ takenOffers, bookingOffer, carrier, bids }, dispatch] = useContext(LoadBoardContext)
  const { ready: isCarrierReady } = carrier || {}

  const isBookDialogOpen = bookingOffer?.id === loadUUID
  const activeBid = bids[loadUUID]
  const { navigateToBookOffer } = useBookingOffer()

  const showOnboardingImprovements = useConfigContext().showOnboardingImprovements.toBoolean()

  const isPriceRange = checkPriceRange(min_price, max_price)

  const isContracted = offerType === OFFER_TYPES.CONTRACTED
  const isLoadCandidate = offerType === OFFER_TYPES.LOAD_CANDIDATE
  const isExpiringLoad = isContracted || isLoadCandidate

  const { analytics } = useAnalytics()

  const bidAvailable = isBidAvailable(
    userIsLoggedIn,
    biddable,
    showOnboardingImprovements,
    isCarrierReady
  )

  const nextBidTime = bid_allowed?.next_bid_time
  const { countdown, countdownHasFinished } = useCountdown(nextBidTime)

  const showBidCountdown = shouldShowBidCountdown({
    carrier,
    countdownHasFinished,
    nextBidTime,
  })
  const bidButtonDisabled = isBidButtonDisabled(
    carrier,
    activeBid,
    showBidCountdown,
    showOnboardingImprovements
  )

  const RMISFinishOnboardingQueryParam = { finishOnboarding: 1 }
  const RMISFinishOnboardingUrl = appendQueryParams(location, RMISFinishOnboardingQueryParam)

  const isBookingAvailable = !userIsLoggedIn || isCarrierReady || showOnboardingImprovements
  const isBookButtonDisabled =
    userIsLoggedIn && (isOfferExpired || !isCarrierReady) && !showOnboardingImprovements

  const redirectToLoadboard = useCallback(() => {
    const nextSearch = location.search ? `${location.search}` : ``
    return history.replace(`/${nextSearch}`)
  }, [history, location.search])

  const handleBid = useCallback(
    event => {
      event.preventDefault()

      const { pathname, search } = location
      const query = qs.parse(search)
      query.bidding = offerUUID
      history.push(`${pathname}?${qs.stringify(query)}`)

      dispatch({
        type: LOAD_BOARD_ACTIONS.OPEN_BID_DIALOG,
        payload: {
          offerUUID: offerUUID,
          triggerContext: 'Load Details',
          price,
          load,
        },
      })

      analytics.tag(['Opened Bid Dialog'])
      analytics.track(['Load Details', 'Bid Button'], {
        triggerContext: 'Load Details',
        offerType,
      })
    },
    [location, offerUUID, history, dispatch, price, analytics, offerType, load]
  )

  const handleBookLinkClick = useCallback(
    event => {
      if (userIsLoggedIn && isCarrierReady) {
        return true
      }

      event.preventDefault()
      return false
    },
    [isCarrierReady, userIsLoggedIn]
  )

  const awardedBid = useAwardedBid(load)
  const bookingType = getLoadBookingType(
    offerType,
    Boolean(awardedBid),
    Boolean(awardedBid?.counteredBid)
  )

  const handleBookOrSignIn = useCallback(() => {
    if (!userIsLoggedIn) {
      analytics.track(['Load Details', 'Sign In Button'], { offerType, loadUUID })
      analytics.tag(['Sign In from Public Page'])
      goToLogin()
      return
    }

    if (!isCarrierReady) {
      history.push(RMISFinishOnboardingUrl)
      return
    }

    onBook?.()
    navigateToBookOffer({ id: loadUUID, triggerContext: 'Load Details', bookingType })
    const triggerName = awardedBid ? 'Confirm Awarded Bid Button' : 'Book Button'
    analytics.track(['Load Details', triggerName], { offerType, loadUUID, loadMode: load.mode })
  }, [
    userIsLoggedIn,
    isCarrierReady,
    onBook,
    navigateToBookOffer,
    loadUUID,
    bookingType,
    awardedBid,
    analytics,
    offerType,
    load.mode,
    history,
    RMISFinishOnboardingUrl,
  ])

  const handleOfferExpiration = useCallback(isExpired => setOfferExpired(isExpired), [])

  function handleRejectButton() {
    setRejectModalOpen(true)
  }

  useEffect(() => {
    if (!isBookDialogOpen && takenOffers.includes(loadUUID)) redirectToLoadboard()
  }, [isBookDialogOpen, loadUUID, takenOffers, redirectToLoadboard])

  const offerPrice = awardedBid ? awardedBid.price : price || min_price
  const actionButtonText = getButtonLabel(awardedBid)
  const { haul, haulPerMile, fuel } = getLoadOfferPriceBreakdown({
    contract: active_offer.contract,
    price: active_offer.price,
    load,
  })
  const ratePerMile = formatCurrency(haulPerMile, true)
  const showStrikeThroughPrice = hasBidPriceChanged(awardedBid)

  return (
    <Layout.Stack gap="none">
      {isExpiringLoad && (
        <ExpirationInfo
          created_at={new Date(active_offer.created_at)}
          expires_at={new Date(active_offer.expires_at)}
          onExpiration={handleOfferExpiration}
          isContracted={isContracted}
          handleRejectButton={handleRejectButton}
          isLoadCandidate={isLoadCandidate}
        />
      )}

      <ActionsFooterInnerContainer
        isPriceRange={isPriceRange}
        isExpiringLoad={isExpiringLoad}
        data-testid="ActionsFooter__Content"
      >
        <Layout.Box padding="spacing-2">
          <Layout.Stack gap="none" align="flex-start">
            <Layout.Group align="center">
              <Layout.Stack gap="none">
                <ActionsFooterPricing>
                  {offerPrice && (
                    <Price
                      price={offerPrice}
                      darkThemed
                      isPriceRange={isPriceRange}
                      large
                      decimals
                    />
                  )}
                  {showStrikeThroughPrice && (
                    <StrikeThroughPrice price={awardedBid.bookPrice} large decimals />
                  )}
                </ActionsFooterPricing>
              </Layout.Stack>
              <Mileage mileage={mileage} darkThemed={!isSmallScreen} />
            </Layout.Group>
            {haulPerMile && <RatePerMile ratePerMile={ratePerMile} />}
            <RateBreakdown haulPrice={haul} fuelPrice={fuel} />
          </Layout.Stack>
        </Layout.Box>
        <ActionsFooterItemGroupContainer>
          {userIsLoggedIn && (
            <OfferChatButton
              stops={stops}
              refNumber={ref_number}
              isLoadCandidate={isLoadCandidate}
              isContracted={isContracted}
              context="Load Details"
              analyticsContext={analytics.prefix}
            />
          )}

          {bidAvailable && (
            <BidButton
              onClick={handleBid}
              activeBid={Boolean(activeBid)}
              disabled={bidButtonDisabled}
            >
              {(showBidCountdown || Boolean(activeBid)) && <StyledSmallLeftIconClock />}
              {showBidCountdown && `Bid again in ${countdown}`}
              {!showBidCountdown && Boolean(activeBid) && `Bid ${formatCurrency(activeBid.price)}`}
              {!showBidCountdown && !Boolean(activeBid) && 'Bid'}
            </BidButton>
          )}

          <BookLink loadId={loadUUID} onClick={handleBookLinkClick} bookingType={bookingType}>
            {!isBookingAvailable && (
              <Tooltip>
                <BookButton disabled>{actionButtonText}</BookButton>

                <Tooltip.Bubble width={256} forceRightOffset>
                  You won’t be able to book loads until your documents are approved
                </Tooltip.Bubble>
              </Tooltip>
            )}

            {isBookingAvailable && (
              <BookButton disabled={isBookButtonDisabled} onClick={handleBookOrSignIn}>
                {isBookDialogOpen ? (
                  <LoadingV2
                    width={16}
                    height={16}
                    data-testid="ActionsFooter__BookButtonLoading"
                  />
                ) : (
                  presentButtonText(actionButtonText, { userIsLoggedIn, isSmallScreen })
                )}
              </BookButton>
            )}
          </BookLink>
        </ActionsFooterItemGroupContainer>
      </ActionsFooterInnerContainer>
    </Layout.Stack>
  )
}

ActionsFooter.propTypes = {
  load: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  setRejectModalOpen: PropTypes.func,
  onBook: PropTypes.func,
}

ActionsFooter.defaultProps = {
  load: {},
  isLoading: false,
  setRejectModalOpen: () => {},
  onBook: () => {},
}

export { ActionsFooter }
